import React, { useState } from "react";
import { useOutletContext } from "react-router-dom";
import { toast, Toaster } from "sonner";
import { Facebook, Instagram, Phone, Mail, MapPin, Send } from "lucide-react";
import { submitContact } from "../lib/api";
import { ADDRESS } from "../lib/images";
import { Reveal } from "../components/site/Reveal";
import { useSeo } from "../components/site/useSeo";

const REASONS = ["General Question", "Catering", "Large Party", "Private Event", "Feedback", "Other"];

const field = "w-full bg-hb-surface border border-hb-line text-hb-cream px-4 py-3 outline-none focus:border-hb-brass transition-colors placeholder:text-hb-mutedlight";

export default function Contact() {
  const { settings } = useOutletContext();
  const s = settings || {};
  const [form, setForm] = useState({ name: "", email: "", phone: "", reason: "General Question", message: "", company: "" });
  const [sending, setSending] = useState(false);
  const directions = `https://www.google.com/maps/dir/?api=1&destination=${ADDRESS.mapsQuery}`;

  useSeo({
    title: "Contact | Hackberry Home — Ben Wheeler, TX",
    description: "Get in touch with Hackberry Home in Ben Wheeler, TX for catering, large parties, private events and more. Southern scratch made kitchen.",
    path: "/contact",
  });

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || form.message.trim().length < 5) {
      toast.error("Please fill in your name, email and a short message.");
      return;
    }
    setSending(true);
    try {
      await submitContact(form);
      toast.success("Thank you! Your message has been received.");
      setForm({ name: "", email: "", phone: "", reason: "General Question", message: "", company: "" });
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="bg-hb-black min-h-screen relative grain">
      <Toaster position="top-center" richColors />
      <div className="max-w-7xl mx-auto px-5 md:px-10 pt-32 pb-24 relative z-10">
        <div className="text-center mb-16">
          <Reveal><p className="eyebrow text-hb-brass mb-4">Say Hello</p></Reveal>
          <Reveal delay={0.05}><h1 className="font-serif text-5xl md:text-8xl text-hb-cream leading-none">We'd Love to<br />Hear From You</h1></Reveal>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">
          {/* info */}
          <div className="lg:col-span-2">
            <Reveal>
              <div className="mb-8">
                <p className="font-serif text-3xl text-hb-cream">Hackberry Home</p>
                <p className="eyebrow text-hb-brass text-[0.62rem] mt-1">Southern Scratch Made Kitchen</p>
              </div>
            </Reveal>
            <Reveal delay={0.05}>
              <ul className="space-y-5">
                <li className="flex items-start gap-3 text-hb-muted">
                  <MapPin size={18} className="text-hb-brass mt-1 shrink-0" />
                  <span>{ADDRESS.line1}<br />{ADDRESS.cityStateZip}</span>
                </li>
                {s.phone && (
                  <li className="flex items-center gap-3 text-hb-muted">
                    <Phone size={18} className="text-hb-brass shrink-0" />
                    <a href={`tel:${(s.phone || "").replace(/[^0-9+]/g, "")}`} className="hover:text-hb-cream transition-colors" data-testid="contact-phone">{s.phone}</a>
                  </li>
                )}
                {s.email && (
                  <li className="flex items-center gap-3 text-hb-muted">
                    <Mail size={18} className="text-hb-brass shrink-0" />
                    <a href={`mailto:${s.email}`} className="hover:text-hb-cream transition-colors" data-testid="contact-email">{s.email}</a>
                  </li>
                )}
              </ul>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="flex gap-3 mt-8">
                {s.facebook && (
                  <a href={s.facebook} target="_blank" rel="noreferrer" aria-label="Facebook" className="h-11 w-11 border border-hb-line flex items-center justify-center text-hb-cream hover:border-hb-brass hover:text-hb-brass transition-colors">
                    <Facebook size={18} />
                  </a>
                )}
                {s.instagram && (
                  <a href={s.instagram} target="_blank" rel="noreferrer" aria-label="Instagram" className="h-11 w-11 border border-hb-line flex items-center justify-center text-hb-cream hover:border-hb-brass hover:text-hb-brass transition-colors">
                    <Instagram size={18} />
                  </a>
                )}
                <a href={directions} target="_blank" rel="noreferrer" className="btn-outline-cream eyebrow px-5 py-3 flex items-center gap-2">
                  <MapPin size={15} /> Directions
                </a>
              </div>
            </Reveal>
          </div>

          {/* form */}
          <div className="lg:col-span-3">
            <Reveal delay={0.1}>
              <form onSubmit={submit} className="border border-hb-line p-7 md:p-10 bg-hb-surface/40" data-testid="contact-form">
                <input type="text" tabIndex={-1} autoComplete="off" value={form.company} onChange={set("company")} className="hidden" aria-hidden="true" />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="eyebrow text-hb-muted text-[0.6rem] block mb-2">Name</label>
                    <input className={field} value={form.name} onChange={set("name")} data-testid="contact-name" required />
                  </div>
                  <div>
                    <label className="eyebrow text-hb-muted text-[0.6rem] block mb-2">Email</label>
                    <input type="email" className={field} value={form.email} onChange={set("email")} data-testid="contact-email-input" required />
                  </div>
                  <div>
                    <label className="eyebrow text-hb-muted text-[0.6rem] block mb-2">Phone</label>
                    <input className={field} value={form.phone} onChange={set("phone")} data-testid="contact-phone-input" />
                  </div>
                  <div>
                    <label className="eyebrow text-hb-muted text-[0.6rem] block mb-2">Reason for Contact</label>
                    <select className={`${field} appearance-none`} value={form.reason} onChange={set("reason")} data-testid="contact-reason">
                      {REASONS.map((r) => <option key={r} value={r} className="bg-hb-surface text-hb-cream">{r}</option>)}
                    </select>
                  </div>
                </div>
                <div className="mt-5">
                  <label className="eyebrow text-hb-muted text-[0.6rem] block mb-2">Message</label>
                  <textarea rows={5} className={field} value={form.message} onChange={set("message")} data-testid="contact-message" required />
                </div>
                <button type="submit" disabled={sending} className="btn-brass eyebrow px-9 py-4 mt-6 flex items-center gap-2 disabled:opacity-60" data-testid="contact-submit">
                  {sending ? "Sending..." : <>Send Message <Send size={15} /></>}
                </button>
              </form>
            </Reveal>
          </div>
        </div>
      </div>
    </div>
  );
}
